import React, { useState, useEffect } from 'react';
import {
  ButtonWapper,
  Heading,
  InfoButton,
  Main,
  SectionLeft,
  SectionRight,
  SubHeading,
  SliderToggle,
  PanelContent,
} from './mapView.style';
import { Button } from '../../../components/uielements';
import { useLocation } from 'react-router-dom';
import { IntlMessages } from '../../../helper/langHelper';
import PropertyOverlaysModal from './modal/PropertyOverlays';
import { ArrowLeft, Info, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import MapWithOverlays from './mapWithOverlays';
import useOverlayStore from '../../../store/overlayStore';
import { getCacheOverlaysScreenshots } from '../../../helper/cacheHelper';
import NewMultipleScreenshotModal from './modal/newMultiScreenCaptureAuto';

const AdminMapView = () => {
  const location = useLocation();
  const searchAddress = location.state?.searchAddress || '';
  const zoneDetailsData = location.state?.zoneDetailsData || '';
  const geoJsonCode = zoneDetailsData?.geoJsonCode || '';

  // Panel collapse state
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);

  // Local state for component-specific data
  const [error, setError] = useState('');
  const [isPropertyOverlaysModalOpen, setIsPropertyOverlaysModalOpen] = useState(false);
  const [isScreenshotModalOpen, setIsScreenshotModalOpen] = useState(false);
  const [screenshotsCompleted, setScreenshotsCompleted] = useState(false);

  // Get overlay data from store for modal and navigation
  const { overlayOptions, isOverlayLoading, addressData } = useOverlayStore();

  useEffect(() => {
    const cacheOverlayScreenshots = getCacheOverlaysScreenshots();

    if (cacheOverlayScreenshots && Array.isArray(cacheOverlayScreenshots)) {
      // Check if all overlays are uploaded (name === true)
      const allUploaded = cacheOverlayScreenshots.some((item) => item.name === true);

      if (allUploaded) {
        // Filter out the completion marker (name === true)
        const validScreenshots = cacheOverlayScreenshots.filter((item) => item.name !== true);

        setScreenshotsCompleted(true);
        setError('');
      }
    }
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const togglePanel = () => {
    setIsPanelCollapsed(!isPanelCollapsed);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleScreenshotComplete = (success) => {
    if (success) {
      setScreenshotsCompleted(true);
      setError('');
    }
  };

  const openScreenshotModal = () => {
    if (overlayOptions.length === 0) {
      setError('No overlays available to capture screenshots.');
      return;
    }
    setIsScreenshotModalOpen(true);
  };

  return (
    <Main role="main" aria-label="Address confirmation page">
      <SectionLeft isCollapsed={isPanelCollapsed}>
        <SliderToggle
          onClick={togglePanel}
          isCollapsed={isPanelCollapsed}
          aria-label={isPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
          className="slider_toggle"
        >
          {isPanelCollapsed ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
        </SliderToggle>

        <PanelContent isCollapsed={isPanelCollapsed}>
          <Heading role="heading" aria-level="1">
            <IntlMessages id="mapview.label.confirm.address" />
          </Heading>
          <SubHeading role="heading" aria-level="2">
            {searchAddress?.address || 'No address provided'}
          </SubHeading>

          {error && (
            <div
              role="alert"
              aria-live="polite"
              style={{
                color: '#d32f2f',
                padding: '10px',
                backgroundColor: '#ffebee',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          {screenshotsCompleted && (
            <div
              role="status"
              aria-live="polite"
              style={{
                color: '#2e7d32',
                padding: '8px',
                backgroundColor: '#e8f5e9',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
            >
              ✓ Generated the report successfully.
            </div>
          )}

          <InfoButton
            onClick={() => setIsPropertyOverlaysModalOpen(true)}
            aria-label="View overlays currently applied to property"
          >
            <span>
              <IntlMessages id="view.property.overlay.list.text" />{' '}
              <Info size={18} aria-hidden="true" color="blue" style={{ marginTop: '5px' }} />
            </span>
          </InfoButton>

          <ButtonWapper>
            <Button onClick={handleBack} aria-label="Go back to previous step">
              <ArrowLeft /> <IntlMessages id="button.label.back" />
            </Button>
            {overlayOptions.length > 0 && !screenshotsCompleted && (
              <Button
                onClick={openScreenshotModal}
                disabled={isOverlayLoading || screenshotsCompleted}
                aria-label="Capture screenshots for all overlays"
                className="capture_screenshot_button"
              >
                <Camera size={18} aria-hidden="true" style={{ marginRight: '8px' }} />
                <span>Capture</span>
              </Button>
            )}
          </ButtonWapper>
        </PanelContent>
      </SectionLeft>

      <SectionRight isCollapsed={isPanelCollapsed}>
        <MapWithOverlays
          searchAddress={searchAddress}
          onError={handleError}
          showDropdown={true}
          key={screenshotsCompleted}
          geoJsonCode={geoJsonCode}
        />
      </SectionRight>

      <PropertyOverlaysModal
        isModalOpen={isPropertyOverlaysModalOpen}
        onClose={() => setIsPropertyOverlaysModalOpen(false)}
        addressOverlays={overlayOptions}
        isFetching={isOverlayLoading}
      />

      <NewMultipleScreenshotModal
        isOpen={isScreenshotModalOpen}
        onClose={() => setIsScreenshotModalOpen(false)}
        searchAddress={addressData || searchAddress}
        overlayOptions={overlayOptions}
        onComplete={handleScreenshotComplete}
        geoJsonCode={geoJsonCode}
      />
    </Main>
  );
};

export default AdminMapView;
